import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../config/api";
import FreshnessDot from "../components/FreshnessDot";
import EarlyWarningsCard from "../components/EarlyWarningsCard";
import MultiBenchmarkChart from "../components/MultiBenchmarkChart";
import AIAnalysisChat from "../components/AIAnalysisChat";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea, ComposedChart
} from "recharts";
import {
  ArrowLeft, RefreshCw, Globe, Clock, TrendingUp, TrendingDown,
  Minus, Droplets, Activity
} from "lucide-react";

const FLAG_EMOJI = {
  JP: "\u{1F1EF}\u{1F1F5}", HK: "\u{1F1ED}\u{1F1F0}", CN: "\u{1F1E8}\u{1F1F3}",
  AU: "\u{1F1E6}\u{1F1FA}", EU: "\u{1F1EA}\u{1F1FA}", DE: "\u{1F1E9}\u{1F1EA}",
  FR: "\u{1F1EB}\u{1F1F7}", GB: "\u{1F1EC}\u{1F1E7}", US: "\u{1F1FA}\u{1F1F8}",
  KR: "\u{1F1F0}\u{1F1F7}", ZA: "\u{1F1FF}\u{1F1E6}", NG: "\u{1F1F3}\u{1F1EC}",
  EG: "\u{1F1EA}\u{1F1EC}", MA: "\u{1F1F2}\u{1F1E6}", KE: "\u{1F1F0}\u{1F1EA}",
  SA: "\u{1F1F8}\u{1F1E6}", AE: "\u{1F1E6}\u{1F1EA}", QA: "\u{1F1F6}\u{1F1E6}",
  KW: "\u{1F1F0}\u{1F1FC}", TR: "\u{1F1F9}\u{1F1F7}", IL: "\u{1F1EE}\u{1F1F1}",
  AR: "\u{1F1E6}\u{1F1F7}", BR: "\u{1F1E7}\u{1F1F7}", CL: "\u{1F1E8}\u{1F1F1}",
  CO: "\u{1F1E8}\u{1F1F4}", PE: "\u{1F1F5}\u{1F1EA}", MX: "\u{1F1F2}\u{1F1FD}",
  IN: "\u{1F1EE}\u{1F1F3}", SG: "\u{1F1F8}\u{1F1EC}", TW: "\u{1F1F9}\u{1F1FC}",
  TH: "\u{1F1F9}\u{1F1ED}", MY: "\u{1F1F2}\u{1F1FE}", ID: "\u{1F1EE}\u{1F1E9}",
  PH: "\u{1F1F5}\u{1F1ED}", NZ: "\u{1F1F3}\u{1F1FF}", CA: "\u{1F1E8}\u{1F1E6}",
  RU: "\u{1F1F7}\u{1F1FA}", ES: "\u{1F1EA}\u{1F1F8}", IT: "\u{1F1EE}\u{1F1F9}",
  CH: "\u{1F1E8}\u{1F1ED}", NL: "\u{1F1F3}\u{1F1F1}", SE: "\u{1F1F8}\u{1F1EA}",
  PL: "\u{1F1F5}\u{1F1F1}", BE: "\u{1F1E7}\u{1F1EA}", AT: "\u{1F1E6}\u{1F1F9}",
  NO: "\u{1F1F3}\u{1F1F4}", DK: "\u{1F1E9}\u{1F1F0}", FI: "\u{1F1EB}\u{1F1EE}",
  PT: "\u{1F1F5}\u{1F1F9}", VN: "\u{1F1FB}\u{1F1F3}",
  GR: "\u{1F1EC}\u{1F1F7}", IE: "\u{1F1EE}\u{1F1EA}",
};

const GlobalMarketsPage = () => {
  const [indicesData, setIndicesData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [seriesConfig, setSeriesConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [timeframe, setTimeframe] = useState("20d");
  const [visibleSeries, setVisibleSeries] = useState({});

  // Cap-Weighted Global Market Index state
  const [globalIndexData, setGlobalIndexData] = useState([]);
  const [globalIndexCurrent, setGlobalIndexCurrent] = useState(null);
  const [globalComposition, setGlobalComposition] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [globalZones, setGlobalZones] = useState(null);
  const [globalRegression, setGlobalRegression] = useState(null);
  const [globalCurrent, setGlobalCurrent] = useState(null);
  const [globalSignals, setGlobalSignals] = useState(null);

  // ETF Performance state (Top/Bottom 20, Heatmap, Volume)
  const [etfPerformance, setEtfPerformance] = useState(null);
  const [volumeSort, setVolumeSort] = useState("volume"); // "volume" or "change"

  // Region-ETF quick-returns strip (1D…5Y) below the timeframe selector
  const [etfQuickReturns, setEtfQuickReturns] = useState(null);
  const [etfQuickLoading, setEtfQuickLoading] = useState(false);
  const [etfQuickError,   setEtfQuickError]   = useState(null);

  // Focus ETF for the MultiBenchmarkChart + Early Warnings panel.
  // Defaults to ACWI (MSCI All-Country World ETF) so the page opens with
  // a genuinely-global reference series; users can flip to any regional
  // ETF (SPY, EWJ, FEZ, EEM, …) to run the full toggle suite on it.
  const [focusEtf, setFocusEtf] = useState("ACWI");

  const fetchIndices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/market/global-indices`, { timeout: 30000 });
      setIndicesData(res.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error fetching global indices:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChartData = useCallback(async () => {
    setChartLoading(true);
    try {
      const res = await axios.get(`${API}/market/global-indices-history`, {
        params: { timeframe },
        timeout: 45000
      });
      setChartData(res.data?.chart_data || []);
      const config = res.data?.series_config || [];
      setSeriesConfig(config);
      // Default: show US + key global indices
      setVisibleSeries(prev => {
        if (Object.keys(prev).length > 0) return prev;
        const defaults = {};
        config.forEach(s => {
          defaults[s.key] = ["sp_500", "nasdaq", "dow_jones", "nikkei_225", "dax", "ftse_100", "jse_all_share", "tadawul"].includes(s.key);
        });
        return defaults;
      });
    } catch (err) {
      console.error("Error fetching chart data:", err);
    } finally {
      setChartLoading(false);
    }
  }, [timeframe]);

  const fetchGlobalIndex = useCallback(async () => {
    setGlobalLoading(true);
    try {
      const res = await axios.get(`${API}/market/global-weighted-index`, {
        params: { timeframe },
        timeout: 45000,
      });
      if (res.data) {
        setGlobalIndexData(res.data.chart_data || []);
        setGlobalIndexCurrent(res.data.current_global_index);
        setGlobalComposition(res.data.composition || []);
        setGlobalZones(res.data.zones || null);
        setGlobalRegression(res.data.regression || null);
        setGlobalCurrent(res.data.current || null);
        setGlobalSignals(res.data.signals || null);
      }
    } catch (err) {
      console.error("Error fetching global weighted index:", err);
    } finally {
      setGlobalLoading(false);
    }
  }, [timeframe]);

  const [etfLoading, setEtfLoading] = useState(false);

  const fetchEtfPerformance = useCallback(async () => {
    setEtfLoading(true);
    setEtfPerformance(null); // Clear old data to show skeleton immediately
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await axios.get(`${API}/market/global-etf-performance`, {
          params: { timeframe },
          timeout: 35000,
        });
        if (res.data && res.data.total_etfs > 0) {
          setEtfPerformance(res.data);
          setEtfLoading(false);
          return;
        }
      } catch (err) {
        console.error(`ETF performance attempt ${attempt + 1} failed:`, err.message);
        if (attempt === 0) await new Promise(r => setTimeout(r, 3000));
      }
    }
    setEtfLoading(false);
  }, [timeframe]);

  useEffect(() => {
    fetchIndices();
    const interval = setInterval(fetchIndices, 300000);
    return () => clearInterval(interval);
  }, [fetchIndices]);

  // Batch quick-returns for every region ETF surfaced on the chart.
  // The endpoint takes ~40s cold (parallel Yahoo v8 fetches) and is
  // then cached for 15 min, so this only "loads slowly" once per page
  // visit.  Independent of the timeframe selector so it doesn't refetch
  // when the user flips 1D↔5Y — the strip always shows all windows.
  const fetchEtfQuickReturns = useCallback(async () => {
    setEtfQuickLoading(true);
    setEtfQuickError(null);
    try {
      const r = await axios.get(`${API}/market/global-etf-quick-returns`, { timeout: 90000 });
      setEtfQuickReturns(r.data || null);
    } catch (e) {
      setEtfQuickReturns(null);
      setEtfQuickError(e?.response?.data?.detail || e?.message || "Failed to load returns");
    } finally {
      setEtfQuickLoading(false);
    }
  }, []);

  useEffect(() => { fetchEtfQuickReturns(); }, [fetchEtfQuickReturns]);

  useEffect(() => {
    // Sequential loading when timeframe changes — avoid semaphore contention
    let cancelled = false;
    const loadTimeframeData = async () => {
      if (cancelled) return;
      await fetchChartData();
      if (cancelled) return;
      await fetchGlobalIndex();
      if (cancelled) return;
      await fetchEtfPerformance();
    };
    loadTimeframeData();
    return () => { cancelled = true; };
  }, [fetchChartData, fetchGlobalIndex, fetchEtfPerformance]);

  const toggleSeries = (key) => {
    setVisibleSeries(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectRegion = (region) => {
    setVisibleSeries(prev => {
      const next = {};
      seriesConfig.forEach(s => {
        if (region === "all") {
          next[s.key] = true;
        } else if (region === "us") {
          next[s.key] = s.region === "Americas";
        } else {
          next[s.key] = s.region === region || s.region === "Americas";
        }
      });
      return next;
    });
  };

  const DirIcon = ({ direction }) => {
    if (direction === "up") return <TrendingUp className="w-3.5 h-3.5 text-green-400" />;
    if (direction === "down") return <TrendingDown className="w-3.5 h-3.5 text-red-400" />;
    return <Minus className="w-3.5 h-3.5 text-gray-400" />;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-3 shadow-xl max-h-72 overflow-y-auto">
        <p className="text-gray-400 text-xs mb-2">{label}</p>
        <div className="space-y-1">
          {payload.map((entry, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-gray-300 w-24 truncate">{entry.name}</span>
              <span className="font-bold" style={{ color: entry.color }}>
                {entry.value >= 0 ? "+" : ""}{entry.value?.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const CapWeightedTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-gray-400 text-xs mb-2">{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-gray-300">{entry.name}:</span>
                <span className="font-bold" style={{ color: entry.color }}>
                  {entry.value?.toFixed(2)}
                  {(entry.name === 'MFI' || entry.name === 'RSI') ? '' : '%'}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderIndexCard = (idx) => (
    <div
      key={idx.ticker}
      className={`bg-[#111] border rounded-lg p-3 ${
        idx.direction === "up" ? "border-green-900/50" :
        idx.direction === "down" ? "border-red-900/50" : "border-gray-800"
      }`}
      data-testid={`global-index-${idx.name.toLowerCase().replace(/[\s\/]/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm flex-shrink-0">{FLAG_EMOJI[idx.flag] || ""}</span>
          <div className="min-w-0">
            <span className="text-xs font-medium text-white truncate block">{idx.name}</span>
            <span className="text-[9px] text-gray-500 truncate block">{idx.country}</span>
          </div>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
          idx.status === "open" ? "bg-green-900/40 text-green-400" : "bg-gray-800 text-gray-400"
        }`}>
          {idx.status === "open" ? "OPEN" : "CLOSED"}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-white">
          {idx.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
        <div className="flex items-center gap-1">
          <DirIcon direction={idx.direction} />
          <span className={`text-sm font-bold ${
            idx.change_pct > 0 ? "text-green-400" : idx.change_pct < 0 ? "text-red-400" : "text-gray-400"
          }`}>
            {idx.change_pct > 0 ? "+" : ""}{idx.change_pct?.toFixed(2)}%
          </span>
        </div>
      </div>
      {/* ETF Row */}
      {idx.etf_ticker ? (
        <div className="mt-2 pt-2 border-t border-gray-800/60 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <a
              href={`/stock?ticker=${idx.etf_ticker}`}
              className="text-xs px-2 py-0.5 rounded bg-blue-900/30 text-blue-400 font-mono font-bold hover:bg-blue-800/50 hover:text-blue-300 transition-colors"
              onClick={(e) => e.stopPropagation()}
              target="_blank" rel="noopener noreferrer"
            >
              {idx.etf_ticker}
            </a>
            <span className="text-[10px] text-gray-500">ETF</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-gray-300 font-medium">
              ${idx.etf_price?.toFixed(2)}
            </span>
            <span className={`text-sm font-bold ${
              idx.etf_change_pct > 0 ? "text-green-400" : idx.etf_change_pct < 0 ? "text-red-400" : "text-gray-400"
            }`}>
              {idx.etf_change_pct > 0 ? "+" : ""}{idx.etf_change_pct?.toFixed(2)}%
            </span>
          </div>
        </div>
      ) : (
        <div className="text-[10px] text-gray-500 mt-1">{idx.country}</div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-200">
      {/* Header */}
      <header className="bg-[#1a1a2e] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="back-to-overview" target="_blank" rel="noopener noreferrer">
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm">Back to Overview</span>
              </a>
              <div className="h-6 w-px bg-gray-700" />
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-400" />
                Global Markets
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <FreshnessDot timestamp={lastUpdate} size="sm" showLabel={true} />
              <button
                onClick={() => { fetchIndices(); fetchChartData(); fetchGlobalIndex(); }}
                disabled={loading || chartLoading || globalLoading}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors"
                data-testid="refresh-global"
              >
                <RefreshCw className={`w-4 h-4 ${(loading || chartLoading) ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* AI CHAT — moved to top (under header/news ticker) */}
        <div className="mb-6">
          <AIAnalysisChat
            mode="market"
            title="Global Markets — AI Chat"
            extraContext={{
              page:            "global-markets",
              timeframe,
              focus_etf:       focusEtf,
              regional_etf_returns: (etfQuickReturns?.rows || []).slice(0, 20).map(r => ({
                sym:   r.ticker,
                name:  r.name,
                region:r.region,
                r1d:   r.ret_1d,
                r5d:   r.ret_5d,
                r20d:  r.ret_20d,
                r50d:  r.ret_50d,
                r1y:   r.ret_1y,
              })),
              global_composite: globalCurrent || null,
              global_signals:   globalSignals || null,
            }}
          />
        </div>

        {/* Index Cards by Region */}
        {indicesData ? (
          <div className="space-y-4 mb-6">
            {/* Asia */}
            {indicesData.asia?.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  Asia-Pacific
                  <span className="text-[10px] text-gray-500 font-normal lowercase tracking-normal">
                    {get_market_status_for_region_label(indicesData.asia)}
                  </span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  {indicesData.asia.map(renderIndexCard)}
                </div>
              </div>
            )}
            {/* Europe */}
            {indicesData.europe?.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  Europe
                  <span className="text-[10px] text-gray-500 font-normal lowercase tracking-normal">
                    {get_market_status_for_region_label(indicesData.europe)}
                  </span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {indicesData.europe.map(renderIndexCard)}
                </div>
              </div>
            )}
            {/* Africa */}
            {indicesData.africa?.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  Africa
                  <span className="text-[10px] text-gray-500 font-normal lowercase tracking-normal">
                    {get_market_status_for_region_label(indicesData.africa)}
                  </span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  {indicesData.africa.map(renderIndexCard)}
                </div>
              </div>
            )}
            {/* Middle East */}
            {indicesData.middle_east?.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  Middle East
                  <span className="text-[10px] text-gray-500 font-normal lowercase tracking-normal">
                    {get_market_status_for_region_label(indicesData.middle_east)}
                  </span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2">
                  {indicesData.middle_east.map(renderIndexCard)}
                </div>
              </div>
            )}
            {/* Latin America */}
            {indicesData.latin_america?.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  Latin America
                  <span className="text-[10px] text-gray-500 font-normal lowercase tracking-normal">
                    {get_market_status_for_region_label(indicesData.latin_america)}
                  </span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2">
                  {indicesData.latin_america.map(renderIndexCard)}
                </div>
              </div>
            )}
            {/* Americas */}
            {indicesData.americas?.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  Americas
                  <span className="text-[10px] text-gray-500 font-normal lowercase tracking-normal">
                    {get_market_status_for_region_label(indicesData.americas)}
                  </span>
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {indicesData.americas.map(renderIndexCard)}
                </div>
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="space-y-6">
            {["Asia-Pacific", "Europe", "Middle East"].map(region => (
              <div key={region}>
                <div className="animate-pulse bg-gray-800/60 rounded h-4 w-24 mb-2" />
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {Array.from({length:6}).map((_,i) => (
                    <div key={i} className="bg-[#111] border border-gray-800 rounded-lg p-3">
                      <div className="animate-pulse bg-gray-800/60 rounded h-3 w-3/4 mb-1.5" />
                      <div className="animate-pulse bg-gray-800/60 rounded h-2.5 w-1/2 mb-2" />
                      <div className="animate-pulse bg-gray-800/60 rounded h-5 w-2/3 mb-1.5" />
                      <div className="animate-pulse bg-gray-800/60 rounded h-2.5 w-full" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Timeframe Selector */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-white">Global vs USA Performance</h2>
          <div className="flex items-center gap-1 bg-[#111] rounded-lg p-1 flex-wrap">
            {[
              { key: "1d",   label: "1D"   },
              { key: "5d",   label: "5D"   },
              { key: "10d",  label: "10D"  },
              { key: "20d",  label: "20D"  },
              { key: "50d",  label: "50D"  },
              { key: "100d", label: "100D" },
              { key: "200d", label: "200D" },
              { key: "1y",   label: "1Y"   },
              { key: "3y",   label: "3Y"   },
              { key: "5y",   label: "5Y"   },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTimeframe(key)}
                className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors ${
                  timeframe === key
                    ? "bg-blue-600 text-white"
                    : "text-gray-400 hover:text-white hover:bg-gray-800"
                }`}
                data-testid={`global-tf-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Region Quick Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs text-gray-500">Show:</span>
          {[
            { key: "all", label: "All Markets" },
            { key: "us", label: "USA Only" },
            { key: "Asia", label: "Asia + USA" },
            { key: "Europe", label: "Europe + USA" },
            { key: "Africa", label: "Africa + USA" },
            { key: "Middle East", label: "Middle East + USA" },
            { key: "Latin America", label: "Latin America + USA" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => selectRegion(key)}
              className="px-2.5 py-1 text-xs rounded bg-[#1a1a2e] text-gray-300 hover:text-white hover:bg-gray-700 border border-gray-700 transition-colors"
              data-testid={`filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Series Toggles */}
        <div className="flex flex-wrap gap-2 mb-4">
          {seriesConfig.map((s) => (
            <button
              key={s.key}
              onClick={() => toggleSeries(s.key)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-all ${
                visibleSeries[s.key]
                  ? "border-opacity-100 text-white"
                  : "border-gray-700 text-gray-500 opacity-50"
              }`}
              style={{
                borderColor: visibleSeries[s.key] ? s.color : undefined,
                backgroundColor: visibleSeries[s.key] ? `${s.color}15` : "transparent",
              }}
              data-testid={`toggle-${s.key}`}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
              <span>{FLAG_EMOJI[s.flag] || ""}</span>
              <span>{s.name}</span>
            </button>
          ))}
        </div>

        {/* Main Chart */}
        <div className="bg-[#111] border border-gray-800 rounded-lg p-4" data-testid="global-chart">
          {chartLoading && chartData.length === 0 ? (
            <div className="h-[500px] flex items-center justify-center">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
                <p className="text-gray-500">Loading global market data...</p>
              </div>
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-[500px] flex items-center justify-center">
              <p className="text-gray-500">No chart data available</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={500}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis
                  dataKey="time"
                  stroke="#666"
                  tick={{ fill: "#888", fontSize: 10 }}
                  tickLine={{ stroke: "#444" }}
                />
                <YAxis
                  stroke="#666"
                  tick={{ fill: "#888", fontSize: 10 }}
                  tickLine={{ stroke: "#444" }}
                  tickFormatter={(v) => `${v}%`}
                  label={{ value: "% Change", angle: -90, position: "insideLeft", fill: "#888", fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" />
                {seriesConfig
                  .filter((s) => visibleSeries[s.key])
                  .map((s) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.name}
                      stroke={s.color}
                      strokeWidth={s.region === "Americas" ? 3 : 2}
                      dot={false}
                      activeDot={{ r: 5, fill: s.color }}
                      connectNulls
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Insight Footer */}
        <div className="mt-4 bg-[#111] border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-white mb-2">How to Read This Chart</h3>
          <p className="text-xs text-gray-400">
            All indices are shown as <strong className="text-white">percentage change</strong> from the start of the selected period.
            This normalizes different price levels so you can directly compare performance.
            When global markets trend down before the US opens, it often signals a lower open for US markets.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-gray-400">Asia-Pacific</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-gray-400">Europe</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-gray-400">Africa</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-gray-400">Middle East</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <span className="text-gray-400">USA</span>
            </div>
          </div>
        </div>


        {/* ============================================================ */}
        {/* QUICK RETURNS — every region ETF, all windows in one table   */}
        {/* ============================================================ */}
        <GlobalETFQuickReturns
          data={etfQuickReturns}
          loading={etfQuickLoading}
          error={etfQuickError}
          onRefresh={fetchEtfQuickReturns}
          focusEtf={focusEtf}
          onFocus={setFocusEtf}
          data-testid="global-etf-quick-returns"
        />

        {/* ============================================================ */}
        {/* FOCUS ETF PANEL — full toggle chart + Early Warnings         */}
        {/* ============================================================ */}
        <div className="mt-8 border-t border-indigo-900/40 pt-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-indigo-400" />
              <h2 className="text-lg font-bold text-white">Focus ETF · Full Analysis</h2>
              <span className="text-[10px] text-gray-500">
                Pick any regional ETF from the strip above to overlay MAs, RSI/MFI, VWAP,
                Support/Resistance and ATR — same toggles as the Stock Analysis page.
              </span>
            </div>
            <FocusEtfPicker
              value={focusEtf}
              options={etfQuickReturns?.rows || []}
              onChange={setFocusEtf}
            />
          </div>

          {/* Full MultiBenchmarkChart with EVERY toggle chip — reuses the
              exact same proven chart from Stock Analysis so users get the
              full MA/Indicator/Volume overlay grid they asked for. */}
          <div className="bg-[#0d0d0d] rounded p-2 mb-4">
            <MultiBenchmarkChart ticker={focusEtf} timeframe={timeframe} />
          </div>

          {/* Early Warnings card scored for the focus ETF — same 4-layer
              scoring users see on the Stock Analysis page. */}
          <div className="mb-4">
            <EarlyWarningsCard ticker={focusEtf} />
          </div>
        </div>

        {/* ============================================================ */}
        {/* ETF PERFORMANCE DASHBOARD                                    */}
        {/* ============================================================ */}
        <div className="mt-10 mb-6 border-t border-emerald-900/40 pt-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Activity className="w-6 h-6 text-emerald-400" />
              <h2 className="text-xl font-bold text-white">Global ETF Dashboard</h2>
              <button onClick={fetchEtfPerformance}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-emerald-400 bg-emerald-900/30 hover:bg-emerald-800/50 rounded-lg border border-emerald-800/50 transition-colors"
                data-testid="refresh-etf-dashboard" title="Reload ETF Data">
                <RefreshCw className={`w-3.5 h-3.5 ${etfLoading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
            <div className="flex items-center gap-1 bg-[#111] rounded-lg p-1 border border-gray-800 flex-wrap">
              {[
                { key: "1d",   label: "1D"   }, { key: "5d",   label: "5D"   },
                { key: "10d",  label: "10D"  }, { key: "20d",  label: "20D"  },
                { key: "50d",  label: "50D"  }, { key: "100d", label: "100D" },
                { key: "200d", label: "200D" }, { key: "1y",   label: "1Y"   },
                { key: "3y",   label: "3Y"   }, { key: "5y",   label: "5Y"   },
              ].map(({ key, label }) => (
                <button key={key} onClick={() => setTimeframe(key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${timeframe === key ? "bg-emerald-600 text-white" : "text-gray-400 hover:text-white hover:bg-gray-800"}`}
                  data-testid={`etf-tf-${key}`}>{label}</button>
              ))}
            </div>
          </div>

          {/* --- TOP 20 & BOTTOM 20 --- */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
            {/* Top 20 */}
            <div className="bg-[#111] border border-green-900/40 rounded-lg">
              <div className="bg-gradient-to-r from-green-900/30 to-[#111] px-4 py-2.5 border-b border-green-900/40 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold text-white">Top 20 ETFs</span>
                <span className="text-xs text-green-400 ml-auto">{etfLoading ? 'Loading...' : `${timeframe.toUpperCase()} Return`}</span>
              </div>
              <div className="divide-y divide-gray-800/50">
                {etfPerformance?.top_20?.length > 0 ? etfPerformance.top_20.map((e, i) => (
                    <a key={e.etf} href={`/stock?ticker=${e.etf}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center px-4 py-2 hover:bg-green-900/10 transition-colors" data-testid={`top20-${i}`}>
                      <span className="text-xs text-gray-500 w-6">{i + 1}</span>
                      <span className="text-sm mr-1">{FLAG_EMOJI[e.flag] || ""}</span>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-bold text-blue-400 font-mono">{e.etf}</span>
                        <span className="text-xs text-gray-500 ml-2 truncate">{e.country}</span>
                      </div>
                      <span className="text-sm font-mono text-gray-400 mr-4">${e.price}</span>
                      <span className={`text-sm font-bold font-mono ${e.pct_return >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {e.pct_return >= 0 ? "+" : ""}{e.pct_return}%
                      </span>
                    </a>
                  )) : (
                    Array.from({length: 10}).map((_, i) => (
                      <div key={i} className="flex items-center px-4 py-2.5 gap-3">
                        <div className="animate-pulse bg-gray-800/60 rounded h-3 w-5" />
                        <div className="animate-pulse bg-gray-800/60 rounded h-3 w-12" />
                        <div className="animate-pulse bg-gray-800/60 rounded h-3 flex-1" />
                        <div className="animate-pulse bg-gray-800/60 rounded h-3 w-16" />
                        <div className="animate-pulse bg-gray-800/60 rounded h-3 w-14" />
                      </div>
                    ))
                  )}
              </div>
            </div>
            {/* Bottom 20 */}
            <div className="bg-[#111] border border-red-900/40 rounded-lg">
              <div className="bg-gradient-to-r from-red-900/30 to-[#111] px-4 py-2.5 border-b border-red-900/40 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-400" />
                <span className="text-sm font-semibold text-white">Bottom 20 ETFs</span>
                <span className="text-xs text-red-400 ml-auto">{timeframe.toUpperCase()} Return</span>
              </div>
              <div className="divide-y divide-gray-800/50">
                {etfPerformance?.bottom_20?.length > 0 ? etfPerformance.bottom_20.map((e, i) => (
                    <a key={e.etf} href={`/stock?ticker=${e.etf}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center px-4 py-2 hover:bg-red-900/10 transition-colors" data-testid={`bottom20-${i}`}>
                      <span className="text-xs text-gray-500 w-6">{i + 1}</span>
                      <span className="text-sm mr-1">{FLAG_EMOJI[e.flag] || ""}</span>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-bold text-blue-400 font-mono">{e.etf}</span>
                        <span className="text-xs text-gray-500 ml-2 truncate">{e.country}</span>
                      </div>
                      <span className="text-sm font-mono text-gray-400 mr-4">${e.price}</span>
                      <span className={`text-sm font-bold font-mono ${e.pct_return >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {e.pct_return >= 0 ? "+" : ""}{e.pct_return}%
                      </span>
                    </a>
                  )) : (
                    Array.from({length: 10}).map((_, i) => (
                      <div key={i} className="flex items-center px-4 py-2.5 gap-3">
                        <div className="animate-pulse bg-gray-800/60 rounded h-3 w-5" />
                        <div className="animate-pulse bg-gray-800/60 rounded h-3 w-12" />
                        <div className="animate-pulse bg-gray-800/60 rounded h-3 flex-1" />
                        <div className="animate-pulse bg-gray-800/60 rounded h-3 w-16" />
                        <div className="animate-pulse bg-gray-800/60 rounded h-3 w-14" />
                      </div>
                    ))
                  )}
              </div>
            </div>
          </div>

          {/* --- REGIONAL HEATMAP --- */}
          {etfPerformance?.regions && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-cyan-400" />
                <h3 className="text-lg font-bold text-white">Regional Heatmap</h3>
                <span className="text-xs text-gray-500 ml-2">{timeframe.toUpperCase()} performance, sized by market cap</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(etfPerformance.regions).map(([region, etfs]) => (
                  <div key={region} className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{region}</div>
                    <div className="flex flex-wrap gap-1">
                      {etfs.map((e) => {
                        const maxCap = Math.max(...etfs.map(x => x.market_cap_b));
                        const relSize = Math.max(0.4, e.market_cap_b / maxCap);
                        const baseH = 48;
                        const h = Math.round(baseH * relSize + 24);
                        const bg = e.pct_return >= 2 ? "bg-green-700" : e.pct_return >= 0.5 ? "bg-green-800/80" : e.pct_return >= 0 ? "bg-green-900/60" : e.pct_return >= -2 ? "bg-red-900/60" : "bg-red-800/80";
                        return (
                          <a key={e.etf} href={`/stock?ticker=${e.etf}`} target="_blank" rel="noopener noreferrer"
                            className={`${bg} rounded px-2 py-1 flex flex-col items-center justify-center hover:brightness-125 transition-all border border-gray-700/30`}
                            style={{ minWidth: `${Math.round(60 * relSize + 40)}px`, height: `${h}px`, flex: `${relSize}` }}
                            title={`${e.etf} (${e.country}): ${e.pct_return >= 0 ? "+" : ""}${e.pct_return}%`}
                            data-testid={`heatmap-${e.etf}`}>
                            <span className="text-xs font-bold text-white">{e.etf}</span>
                            <span className={`text-[10px] font-mono font-bold ${e.pct_return >= 0 ? "text-green-300" : "text-red-300"}`}>
                              {e.pct_return >= 0 ? "+" : ""}{e.pct_return}%
                            </span>
                            {relSize > 0.5 && <span className="text-[9px] text-gray-400">{e.country}</span>}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-gray-500">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-700" /> +2%+</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-900/60" /> 0-2%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-900/60" /> 0 to -2%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-800/80" /> -2%+</span>
              </div>
            </div>
          )}

          {/* --- VOLUME ANALYSIS --- */}
          {etfPerformance && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-blue-400" />
                  <h3 className="text-lg font-bold text-white">Volume Analysis</h3>
                </div>
                <div className="flex items-center gap-1 bg-[#111] rounded-lg p-1">
                  <button onClick={() => setVolumeSort("volume")}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${volumeSort === "volume" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white hover:bg-gray-800"}`}
                    data-testid="vol-sort-absolute">Highest Volume</button>
                  <button onClick={() => setVolumeSort("change")}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${volumeSort === "change" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white hover:bg-gray-800"}`}
                    data-testid="vol-sort-change">% Change vs Avg</button>
                </div>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="volume-table">
                  <thead className="bg-[#0d0d0d] text-gray-500 text-xs">
                    <tr>
                      <th className="text-left px-4 py-2">#</th>
                      <th className="text-left px-4 py-2">ETF</th>
                      <th className="text-left px-4 py-2">Country</th>
                      <th className="text-right px-4 py-2">Price</th>
                      <th className="text-right px-4 py-2">{timeframe.toUpperCase()} Return</th>
                      <th className="text-right px-4 py-2">Volume</th>
                      <th className="text-right px-4 py-2">20d Avg Vol</th>
                      <th className="text-right px-4 py-2">Vol % Change</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {(volumeSort === "volume" ? etfPerformance.by_volume : etfPerformance.by_volume_change)?.map((e, i) => (
                      <tr key={e.etf} className="hover:bg-gray-800/30 transition-colors">
                        <td className="px-4 py-2 text-xs text-gray-500">{i + 1}</td>
                        <td className="px-4 py-2">
                          <a href={`/stock?ticker=${e.etf}`} target="_blank" rel="noopener noreferrer"
                            className="text-blue-400 font-mono font-bold hover:text-blue-300">
                            {FLAG_EMOJI[e.flag] || ""} {e.etf}
                          </a>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-400">{e.country}</td>
                        <td className="px-4 py-2 text-right font-mono text-gray-300">${e.price}</td>
                        <td className={`px-4 py-2 text-right font-mono font-bold ${e.pct_return >= 0 ? "text-green-400" : "text-red-400"}`}>
                          {e.pct_return >= 0 ? "+" : ""}{e.pct_return}%
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-gray-300">
                          {e.volume >= 1e6 ? `${(e.volume / 1e6).toFixed(1)}M` : e.volume >= 1e3 ? `${(e.volume / 1e3).toFixed(0)}K` : e.volume}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-gray-500">
                          {e.avg_volume_20d >= 1e6 ? `${(e.avg_volume_20d / 1e6).toFixed(1)}M` : e.avg_volume_20d >= 1e3 ? `${(e.avg_volume_20d / 1e3).toFixed(0)}K` : e.avg_volume_20d}
                        </td>
                        <td className={`px-4 py-2 text-right font-mono font-bold ${e.volume_change_pct >= 0 ? "text-cyan-400" : "text-orange-400"}`}>
                          {e.volume_change_pct >= 0 ? "+" : ""}{e.volume_change_pct}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ============================================================ */}
        {/* CAP-WEIGHTED GLOBAL MARKET INDEX SECTION                     */}
        {/* ============================================================ */}
        <div className="mt-12 mb-6 border-t border-cyan-900/40 pt-8">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-7 h-7 text-cyan-400" />
            <div>
              <h2 className="text-xl font-bold text-white">Cap-Weighted Global Market Index</h2>
              <p className="text-xs text-gray-500">18 indices across Asia, Europe, Africa & Middle East weighted by market cap</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {globalRegression && (
                <span className="text-xs px-2.5 py-1 rounded font-bold" style={{ backgroundColor: `${globalRegression.trend_color}22`, color: globalRegression.trend_color }}>
                  {globalRegression.trend}
                </span>
              )}
              {globalRegression && (
                <span className="text-xs text-gray-400">R² {globalRegression.r_squared?.toFixed(3)}</span>
              )}
              {globalSignals?.near_support && (
                <span className="text-[10px] px-2 py-0.5 rounded bg-green-900/30 text-green-400 font-medium">NEAR SUPPORT</span>
              )}
              {globalSignals?.near_resistance && (
                <span className="text-[10px] px-2 py-0.5 rounded bg-red-900/30 text-red-400 font-medium">NEAR RESISTANCE</span>
              )}
            </div>
            {globalIndexCurrent !== null && (
              <div className={`ml-auto text-2xl font-bold ${globalIndexCurrent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {globalIndexCurrent >= 0 ? '+' : ''}{globalIndexCurrent?.toFixed(3)}%
              </div>
            )}
          </div>

          {/* Zones Summary Cards */}
          {globalZones && globalCurrent && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
              <div className="bg-[#111] border border-gray-800 rounded-lg p-2.5 text-center">
                <div className="text-[10px] text-gray-500 uppercase">Price</div>
                <div className="text-base font-bold text-white">{globalCurrent.price?.toFixed(2)}</div>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-lg p-2.5 text-center">
                <div className="text-[10px] text-gray-500 uppercase">Support</div>
                <div className="text-base font-bold text-green-400">{globalZones.support?.toFixed(2)}</div>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-lg p-2.5 text-center">
                <div className="text-[10px] text-gray-500 uppercase">Resistance</div>
                <div className="text-base font-bold text-red-400">{globalZones.resistance?.toFixed(2)}</div>
              </div>
              <div className="bg-[#111] border border-green-900/40 rounded-lg p-2.5 text-center">
                <div className="text-[10px] text-green-500 uppercase">Entry Zone</div>
                <div className="text-xs font-bold text-green-400">{globalZones.entry_zone?.low?.toFixed(2)} - {globalZones.entry_zone?.high?.toFixed(2)}</div>
              </div>
              <div className="bg-[#111] border border-red-900/40 rounded-lg p-2.5 text-center">
                <div className="text-[10px] text-red-500 uppercase">Exit Zone</div>
                <div className="text-xs font-bold text-red-400">{globalZones.exit_zone?.low?.toFixed(2)} - {globalZones.exit_zone?.high?.toFixed(2)}</div>
              </div>
              {/* VIX Tile */}
              {(() => {
                const lastVix = [...globalIndexData].reverse().find(p => p.vix != null)?.vix;
                const vixLevel = lastVix || 0;
                return (
                  <div className={`bg-[#111] border rounded-lg p-2.5 text-center ${
                    vixLevel >= 30 ? 'border-red-700/60' :
                    vixLevel >= 20 ? 'border-orange-700/60' :
                    'border-gray-800'
                  }`} data-testid="global-vix-tile">
                    <div className="text-[10px] text-orange-400 uppercase">VIX</div>
                    <div className="text-base font-bold text-white">{lastVix ? lastVix.toFixed(2) : '—'}</div>
                    <div className={`text-[10px] font-bold uppercase ${
                      vixLevel >= 30 ? 'text-red-400' :
                      vixLevel >= 20 ? 'text-orange-400' :
                      vixLevel >= 15 ? 'text-yellow-400' :
                      'text-green-400'
                    }`}>
                      {vixLevel >= 30 ? 'EXTREME FEAR' :
                       vixLevel >= 20 ? 'HIGH FEAR' :
                       vixLevel >= 15 ? 'MODERATE' : lastVix ? 'LOW FEAR' : '—'}
                    </div>
                  </div>
                );
              })()}
              {/* Trend Strength Tile */}
              <div className="bg-[#111] border border-gray-800 rounded-lg p-2.5 text-center" data-testid="global-trend-tile">
                <div className="text-[10px] text-purple-400 uppercase">Trend Strength</div>
                <div className="text-base font-bold text-white">{globalRegression ? `${(globalRegression.r_squared * 100).toFixed(0)}%` : '—'}</div>
                <div className="text-[10px] text-gray-500">R² Correlation</div>
              </div>
            </div>
          )}

          {/* Chart 1: Cap-Weighted Global Market Index with Price, Regression, SMAs, Zones */}
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4 mb-6" data-testid="global-index-chart">
            <h3 className="text-base font-semibold text-cyan-400 mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Cap-Weighted Global Market Index — Price, Regression, SMA 20, SMA 50
            </h3>
            {globalLoading && globalIndexData.length === 0 ? (
              <div className="h-[400px] flex items-center justify-center">
                <RefreshCw className="w-6 h-6 animate-spin text-cyan-500" />
              </div>
            ) : globalIndexData.length === 0 ? (
              <div className="h-[400px] flex items-center justify-center text-gray-500">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={globalIndexData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="time" stroke="#666" tick={{ fill: '#888', fontSize: 10 }} tickLine={{ stroke: '#444' }} />
                  <YAxis
                    domain={['auto', 'auto']}
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                    label={{ value: 'Index Level', angle: -90, position: 'insideLeft', fill: '#06b6d4', fontSize: 10 }}
                  />
                  <Tooltip content={<CapWeightedTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="circle" />
                  <ReferenceArea
                    y1={globalZones?.entry_zone?.low}
                    y2={globalZones?.entry_zone?.high}
                    fill="#22c55e"
                    fillOpacity={0.08}
                    label={{ value: 'ENTRY ZONE', fill: '#22c55e', fontSize: 9, position: 'insideTopLeft' }}
                  />
                  <ReferenceArea
                    y1={globalZones?.exit_zone?.low}
                    y2={globalZones?.exit_zone?.high}
                    fill="#ef4444"
                    fillOpacity={0.08}
                    label={{ value: 'EXIT ZONE', fill: '#ef4444', fontSize: 9, position: 'insideTopLeft' }}
                  />
                  <ReferenceLine y={globalZones?.support} stroke="#22c55e" strokeDasharray="5 5" label={{ value: `S: ${globalZones?.support?.toFixed(1)}`, fill: '#22c55e', fontSize: 9, position: 'left' }} />
                  <ReferenceLine y={globalZones?.resistance} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `R: ${globalZones?.resistance?.toFixed(1)}`, fill: '#ef4444', fontSize: 9, position: 'left' }} />
                  <Line type="monotone" dataKey="sma50" name="SMA 50" stroke="#ffffff" strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls />
                  <Line type="monotone" dataKey="sma20" name="SMA 20" stroke="#ffffff" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls opacity={0.6} />
                  <Line type="monotone" dataKey="regression" name="Regression" stroke="#a855f7" strokeWidth={2} strokeDasharray="8 4" dot={false} connectNulls />
                  <Line type="monotone" dataKey="price" name="Global Index Price" stroke="#06b6d4" strokeWidth={3} dot={false} activeDot={{ r: 6, fill: '#06b6d4' }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Chart 2: MFI, RSI & Global Weighted Market Index */}
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4 mb-6" data-testid="global-mfi-rsi-chart">
            <h3 className="text-base font-semibold text-cyan-400 mb-4 flex items-center gap-2">
              <Droplets className="w-4 h-4 text-green-400" />
              MFI, RSI & Global Weighted Market Index
            </h3>
            {globalLoading && globalIndexData.length === 0 ? (
              <div className="h-[350px] flex items-center justify-center">
                <RefreshCw className="w-6 h-6 animate-spin text-green-500" />
              </div>
            ) : globalIndexData.length === 0 ? (
              <div className="h-[350px] flex items-center justify-center text-gray-500">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={globalIndexData} margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="time" stroke="#666" tick={{ fill: '#888', fontSize: 10 }} tickLine={{ stroke: '#444' }} />
                  <YAxis
                    yAxisId="indicators"
                    domain={[0, 100]}
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                    label={{ value: 'MFI / RSI (0-100)', angle: -90, position: 'insideLeft', fill: '#888', fontSize: 10 }}
                  />
                  <YAxis
                    yAxisId="price"
                    orientation="right"
                    domain={['auto', 'auto']}
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                    label={{ value: 'Index Level', angle: 90, position: 'insideRight', fill: '#06b6d4', fontSize: 10 }}
                  />
                  <Tooltip content={<CapWeightedTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="circle" />
                  <ReferenceLine yAxisId="indicators" y={70} stroke="#f97316" strokeDasharray="5 5" label={{ value: 'OB (70)', fill: '#f97316', fontSize: 9, position: 'left' }} />
                  <ReferenceLine yAxisId="indicators" y={50} stroke="#6b7280" strokeDasharray="3 3" />
                  <ReferenceLine yAxisId="indicators" y={30} stroke="#84cc16" strokeDasharray="5 5" label={{ value: 'OS (30)', fill: '#84cc16', fontSize: 9, position: 'left' }} />
                  <Line yAxisId="indicators" type="monotone" dataKey="mfi" name="MFI" stroke="#22c55e" strokeWidth={3} dot={false} activeDot={{ r: 6, fill: '#22c55e' }} connectNulls />
                  <Line yAxisId="indicators" type="monotone" dataKey="rsi" name="RSI" stroke="#ef4444" strokeWidth={3} dot={false} activeDot={{ r: 6, fill: '#ef4444' }} connectNulls />
                  <Line yAxisId="price" type="monotone" dataKey="price" name="Global Index" stroke="#06b6d4" strokeWidth={3} dot={false} activeDot={{ r: 6, fill: '#06b6d4' }} connectNulls />
                  <Line yAxisId="price" type="monotone" dataKey="regression" name="Regression" stroke="#a855f7" strokeWidth={1.5} strokeDasharray="8 4" dot={false} connectNulls />
                  <Line yAxisId="price" type="monotone" dataKey="sma20" name="SMA 20" stroke="#eab308" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Chart 3: VIX & Global Weighted Market Index */}
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4 mb-6" data-testid="global-vix-chart">
            <h3 className="text-base font-semibold text-cyan-400 mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4 text-orange-400" />
              VIX & Global Weighted Market Index
            </h3>
            {globalLoading && globalIndexData.length === 0 ? (
              <div className="h-[350px] flex items-center justify-center">
                <RefreshCw className="w-6 h-6 animate-spin text-orange-500" />
              </div>
            ) : globalIndexData.length === 0 ? (
              <div className="h-[350px] flex items-center justify-center text-gray-500">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={globalIndexData} margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="time" stroke="#666" tick={{ fill: '#888', fontSize: 10 }} tickLine={{ stroke: '#444' }} />
                  <YAxis
                    yAxisId="vix"
                    domain={['auto', 'auto']}
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                    label={{ value: 'VIX Level', angle: -90, position: 'insideLeft', fill: '#f97316', fontSize: 10 }}
                  />
                  <YAxis
                    yAxisId="price"
                    orientation="right"
                    domain={['auto', 'auto']}
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 10 }}
                    tickLine={{ stroke: '#444' }}
                    label={{ value: 'Index Level', angle: 90, position: 'insideRight', fill: '#06b6d4', fontSize: 10 }}
                  />
                  <Tooltip content={<CapWeightedTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="circle" />
                  <ReferenceLine yAxisId="vix" y={30} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Fear (30)', fill: '#ef4444', fontSize: 9, position: 'left' }} />
                  <ReferenceLine yAxisId="vix" y={20} stroke="#f97316" strokeDasharray="5 5" label={{ value: 'High (20)', fill: '#f97316', fontSize: 9, position: 'left' }} />
                  <ReferenceArea yAxisId="price" y1={globalZones?.entry_zone?.low} y2={globalZones?.entry_zone?.high} fill="#22c55e" fillOpacity={0.06} />
                  <ReferenceArea yAxisId="price" y1={globalZones?.exit_zone?.low} y2={globalZones?.exit_zone?.high} fill="#ef4444" fillOpacity={0.06} />
                  <Line yAxisId="vix" type="monotone" dataKey="vix" name="VIX" stroke="#f97316" strokeWidth={3} dot={false} activeDot={{ r: 6, fill: '#f97316' }} connectNulls />
                  <Line yAxisId="price" type="monotone" dataKey="price" name="Global Index" stroke="#06b6d4" strokeWidth={3} dot={false} activeDot={{ r: 6, fill: '#06b6d4' }} connectNulls />
                  <Line yAxisId="price" type="monotone" dataKey="sma20" name="SMA 20" stroke="#eab308" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls />
                  <Line yAxisId="price" type="monotone" dataKey="sma50" name="SMA 50" stroke="#ffffff" strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Global Index Composition */}
          {globalComposition.length > 0 && (
            <div className="bg-[#111] border border-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-white mb-3">Global Index Composition (Market Cap Weighted)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
                {globalComposition.map((c) => (
                  <div key={c.name} className="flex items-center gap-2">
                    <span className="text-gray-400">{c.name}</span>
                    <span className="text-cyan-400 font-bold">{c.weight_pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

function get_market_status_for_region_label(indices) {
  if (!indices?.length) return "";
  const open = indices.some(i => i.status === "open");
  return open ? "Markets open" : "Markets closed";
}

// ──────────────────────────────────────────────────────────────────────
// Quick-returns strip for every region ETF surfaced on the chart.
// Shows 1D / 5D / 10D / 20D / 50D / 100D / 200D / 1Y / 3Y / 5Y as a
// dense table grouped by region.  Clicking a row sets the Focus ETF
// used by the MultiBenchmarkChart + Early Warnings panel below.
// ──────────────────────────────────────────────────────────────────────
const QR_WINDOWS = [
  { key: "ret_1d",   label: "1D"   },
  { key: "ret_5d",   label: "5D"   },
  { key: "ret_10d",  label: "10D"  },
  { key: "ret_20d",  label: "20D"  },
  { key: "ret_50d",  label: "50D"  },
  { key: "ret_100d", label: "100D" },
  { key: "ret_200d", label: "200D" },
  { key: "ret_1y",   label: "1Y"   },
  { key: "ret_3y",   label: "3Y"   },
  { key: "ret_5y",   label: "5Y"   },
];
const _fmtPct = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`);
const _pctCls = (v) =>
  v == null ? "text-gray-500" : v >= 0 ? "text-green-400" : "text-red-400";

// Convert a 2-letter ISO country code (e.g. "US", "JP", "EU") into its
// flag emoji using regional indicator symbols.  Falls back to a globe
// glyph for missing/invalid codes.
const flagEmoji = (cc) => {
  if (!cc || typeof cc !== "string" || cc.length !== 2) return "🌐";
  const base = 127397; // 'A' - 65 = 65 → 0x1F1E6 codepoint offset
  return String.fromCodePoint(
    ...cc.toUpperCase().split("").map((c) => base + c.charCodeAt(0))
  );
};

function GlobalETFQuickReturns({ data, loading, error, onRefresh, focusEtf, onFocus }) {
  const rows = data?.rows || [];

  // Column sort state — click a timeframe header to sort desc, click again
  // for asc, third click clears back to the default region/country grouping.
  // Also supports sorting by Price / Region / ETF / Name.
  const [sortKey, setSortKey] = useState(null);       // null → default region/country order
  const [sortDir, setSortDir] = useState("desc");     // "desc" | "asc"

  const cycleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("desc"); return; }
    if (sortDir === "desc") { setSortDir("asc"); return; }
    setSortKey(null); setSortDir("desc");
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      const aNull = av == null; const bNull = bv == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;   // nulls always sink
      if (bNull) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
      }
      return sortDir === "desc" ? bv - av : av - bv;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  // Small chevron indicator for the active column
  const sortIndicator = (key) => {
    if (sortKey !== key) return null;
    return <span className="ml-1 text-white">{sortDir === "desc" ? "▼" : "▲"}</span>;
  };

  return (
    <div className="mt-8 border-t border-cyan-900/40 pt-6" data-testid="global-etf-quick-returns">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-bold text-white">Regional ETF Returns</h2>
          <span className="text-[10px] text-gray-500">
            {loading
              ? "Loading Yahoo daily bars for every region ETF (~40s cold, 15 min cache)…"
              : error
                ? "Load failed — click Refresh"
                : `${rows.length} ETFs · click any timeframe header to sort · click a row to focus the chart below`}
          </span>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="global-qr-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && !loading && (
        <div className="bg-amber-900/20 border border-amber-700 rounded p-2 mb-2 text-[11px] text-amber-200">
          {error}
        </div>
      )}

      <div className="bg-[#0d0d0d] border border-gray-800 rounded overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="global-qr-table">
          <thead className="bg-[#111] select-none">
            <tr className="text-gray-400 uppercase tracking-wider">
              <th
                onClick={() => cycleSort("region")}
                className="text-left px-3 py-2 font-semibold cursor-pointer hover:text-white"
                data-testid="global-qr-sort-region"
              >
                Region {sortIndicator("region")}
              </th>
              <th
                onClick={() => cycleSort("country")}
                className="text-left px-3 py-2 font-semibold cursor-pointer hover:text-white"
                data-testid="global-qr-sort-country"
              >
                Country {sortIndicator("country")}
              </th>
              <th
                onClick={() => cycleSort("ticker")}
                className="text-left px-3 py-2 font-semibold cursor-pointer hover:text-white"
                data-testid="global-qr-sort-ticker"
              >
                ETF {sortIndicator("ticker")}
              </th>
              <th
                onClick={() => cycleSort("name")}
                className="text-left px-3 py-2 font-semibold cursor-pointer hover:text-white"
                data-testid="global-qr-sort-name"
              >
                Name {sortIndicator("name")}
              </th>
              <th
                onClick={() => cycleSort("price")}
                className="text-right px-3 py-2 font-semibold cursor-pointer hover:text-white"
                data-testid="global-qr-sort-price"
              >
                Price {sortIndicator("price")}
              </th>
              {QR_WINDOWS.map(w => (
                <th
                  key={w.key}
                  onClick={() => cycleSort(w.key)}
                  className="text-right px-2 py-2 font-semibold cursor-pointer hover:text-white"
                  data-testid={`global-qr-sort-${w.key}`}
                  title={`Sort by ${w.label} return`}
                >
                  {w.label}
                  {sortIndicator(w.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/70">
            {loading && rows.length === 0 && (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={5 + QR_WINDOWS.length} className="px-3 py-2">
                    <div className="h-3 bg-gray-800/70 rounded animate-pulse" />
                  </td>
                </tr>
              ))
            )}
            {sortedRows.map((r) => {
              const active = r.ticker === focusEtf;
              return (
                <tr
                  key={r.ticker}
                  onClick={() => onFocus?.(r.ticker)}
                  className={`cursor-pointer transition-colors ${
                    active ? "bg-indigo-900/40" : "hover:bg-gray-800/40"
                  }`}
                  data-testid={`global-qr-row-${r.ticker}`}
                >
                  <td className="px-3 py-1.5 text-gray-400">{r.region}</td>
                  <td className="px-3 py-1.5 text-white whitespace-nowrap">
                    <span className="mr-1.5" aria-hidden="true">{flagEmoji(r.flag)}</span>
                    {r.country}
                  </td>
                  <td className="px-3 py-1.5 font-mono font-bold">
                    {/* ETF ticker → Stock Analysis page.  stopPropagation
                        so clicking the link doesn't also fire the row's
                        onFocus (which sets the ticker as the Focus ETF
                        for the chart below). */}
                    <a
                      href={`/stock?ticker=${encodeURIComponent(r.ticker)}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-cyan-300 hover:text-cyan-200 hover:underline"
                      data-testid={`global-qr-link-${r.ticker}`}
                      title={`Open Stock Analysis for ${r.ticker}`}
                    >
                      {r.ticker}
                    </a>
                  </td>
                  <td className="px-3 py-1.5 text-white truncate max-w-[220px]">{r.name}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-white">
                    {r.price != null ? `$${r.price.toFixed(2)}` : "—"}
                  </td>
                  {QR_WINDOWS.map(w => (
                    <td key={w.key} className={`px-2 py-1.5 text-right font-mono font-semibold ${_pctCls(r[w.key])}`}>
                      {_fmtPct(r[w.key])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Compact button strip for the Focus ETF panel — one clickable chip per
// regional ETF, grouped by region so the user can quickly flip between
// countries without hunting through a dropdown.  Uses horizontal scroll
// on narrow viewports.
function FocusEtfPicker({ value, options, onChange }) {
  // Group ETFs by region so the strip is scannable — Americas first (US
  // is the natural comparison benchmark), then Asia / Europe / Middle
  // East / Africa / Latin America.  Any region not in the priority list
  // sorts alphabetically at the end.
  const REGION_ORDER = [
    "Americas", "Asia", "Europe", "Middle East", "Africa", "Latin America",
  ];
  const byRegion = useMemo(() => {
    const m = new Map();
    for (const o of options) {
      const r = o.region || "Other";
      if (!m.has(r)) m.set(r, []);
      m.get(r).push(o);
    }
    return REGION_ORDER
      .filter(r => m.has(r))
      .concat([...m.keys()].filter(r => !REGION_ORDER.includes(r)).sort())
      .map(r => [r, m.get(r)]);
  }, [options]);

  return (
    <div className="flex flex-col gap-1 max-w-[70vw]" data-testid="focus-etf-picker">
      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        Focus ETF · click to overlay in the chart below
      </span>
      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto pr-1">
        {byRegion.map(([region, group]) => (
          <div key={region} className="flex items-center gap-1 mr-2">
            <span className="text-[9px] uppercase tracking-wider text-gray-600 font-semibold">
              {region}:
            </span>
            {group.map((o) => {
              const active = o.ticker === value;
              return (
                <button
                  key={o.ticker}
                  type="button"
                  onClick={() => onChange?.(o.ticker)}
                  title={`${o.ticker} · ${o.name} (${o.country})`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono font-bold rounded border transition-colors ${
                    active
                      ? "bg-indigo-600 border-indigo-400 text-white"
                      : "bg-black border-gray-700 text-cyan-300 hover:border-indigo-500 hover:text-white"
                  }`}
                  data-testid={`focus-etf-btn-${o.ticker}`}
                >
                  <span aria-hidden="true">{flagEmoji(o.flag)}</span>
                  {o.ticker}
                </button>
              );
            })}
          </div>
        ))}
        {/* If the current value isn't in the options list yet (e.g. the
            batch endpoint is still loading), keep the active pill visible
            so the layout doesn't flash empty. */}
        {options.length === 0 && (
          <button
            type="button"
            className="px-2 py-0.5 text-[10px] font-mono font-bold rounded border bg-indigo-600 border-indigo-400 text-white"
          >
            {value}
          </button>
        )}
      </div>
    </div>
  );
}

export default GlobalMarketsPage;
